import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/brand";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2 } from "lucide-react";

function UserRow({ u, mentors, onAssign, onRole, onToggle, onDelete }) {
  return (
    <div className="p-4 flex flex-wrap items-center gap-3" data-testid={`user-row-${u.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="font-bold flex items-center gap-2">
          {u.name}
          {!u.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full border border-ink bg-[#FFDede]">inactive</span>}
        </div>
        <div className="text-xs text-muted-foreground">{u.email}{u.organization_name ? ` · ${u.organization_name}` : ""}</div>
      </div>

      {u.role === "student" && (
        <Select value={u.mentor_id || ""} onValueChange={(v) => onAssign(v === "none" ? null : v)}>
          <SelectTrigger className="w-44 h-9 border-2 border-ink rounded-xl text-xs" data-testid={`assign-mentor-${u.id}`}>
            <SelectValue placeholder="Assign mentor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Unassigned</SelectItem>
            {mentors.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {["student", "mentor"].includes(u.role) ? (
        <Select value={u.role} onValueChange={onRole}>
          <SelectTrigger className="w-36 h-9 border-2 border-ink rounded-xl text-xs" data-testid={`role-select-${u.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="student">Student</SelectItem>
            <SelectItem value="mentor">Mentor</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <span className="w-36 h-9 grid place-items-center rounded-xl border-2 border-ink text-xs font-bold bg-[#FFFDF5]" data-testid={`role-label-${u.id}`}>{ROLE_LABELS[u.role]}</span>
      )}

      <button onClick={onToggle} data-testid={`toggle-active-${u.id}`} className="h-9 px-3 rounded-xl border-2 border-ink text-xs font-bold press bg-white">
        {u.is_active ? "Deactivate" : "Activate"}
      </button>

      {!u.is_active && u.role !== "admin" && (
        <button onClick={onDelete} data-testid={`delete-user-${u.id}`}
          className="h-9 w-9 grid place-items-center rounded-xl border-2 border-ink press"
          style={{ background: "#FF3B7A", color: "#FFFDF5" }} title="Delete permanently">
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

export function UsersTab() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    apiGet("/manage/users").then(setUsers).catch((e) => toast.error(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const mentors = users.filter((u) => u.role === "mentor");

  const changeRole = async (id, role) => {
    try { await apiPatch(`/manage/users/${id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const toggleActive = async (u) => {
    try { await apiPatch(`/manage/users/${u.id}/active`, { is_active: !u.is_active }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}? This removes all their data and cannot be undone.`)) return;
    try { await apiDelete(`/manage/users/${u.id}`); toast.success(`${u.name} deleted`); load(); }
    catch (e) { toast.error(e.message); }
  };
  const assignMentor = async (studentId, mentorId) => {
    try { await apiPost("/manage/assignments", { mentor_id: mentorId || null, student_ids: [studentId] }); toast.success("Mentor assigned"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const shown = filter === "all" ? users : users.filter((u) => u.role === filter);

  return (
    <div data-testid="users-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        {["all", "student", "mentor", "org_leader", "admin"].map((r) => (
          <button key={r} onClick={() => setFilter(r)} data-testid={`user-filter-${r}`}
            className={`px-3 py-1.5 rounded-full border-2 border-ink text-xs font-bold press ${filter === r ? "bg-primary text-primary-foreground" : "bg-white"}`}>
            {r === "all" ? "All" : ROLE_LABELS[r]}
          </button>
        ))}
      </div>

      <div className="rounded-3xl border-2 border-ink bg-white overflow-hidden shadow-pop-sm">
        <div className="divide-y-2 divide-[#ECE7D8]">
          {shown.map((u) => (
            <UserRow key={u.id} u={u} mentors={mentors}
              onAssign={(m) => assignMentor(u.id, m)} onRole={(r) => changeRole(u.id, r)}
              onToggle={() => toggleActive(u)} onDelete={() => deleteUser(u)} />
          ))}
          {shown.length === 0 && <p className="p-6 text-sm text-muted-foreground">No users.</p>}
        </div>
      </div>
    </div>
  );
}
